'use client';

import type { ListHomeLoveMainInstitutionSpecialtyDto } from '__generated__/api/unauth';
import { cn } from '@core/utils';
import { sampleSize } from 'es-toolkit';
import { useEffect, useMemo, useRef, useState } from 'react';

import { CustomLink } from '@/app/_components/CustomLink';
import { NewBadge } from '@/app/donation/_components/NewBadge';
import { HorizontalScroll, Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';

import styles from './RegionalSpecialtiesSection.module.scss';

interface SpecialtiesItemProps {
  imageUrl: string;
  specialtyName: string;
  institutionName: string;
  href: string;
  isNew?: boolean;
  className?: string;
}

/** 모바일, 태블릿에서 사용되는 컴포넌트 */
const SpecialtiesItem = ({
  imageUrl,
  specialtyName,
  institutionName,
  href,
  isNew,
  className,
}: SpecialtiesItemProps) => {
  return (
    <CustomLink
      href={href}
      className={cn(
        'flex w-56 shrink-0 flex-col items-center gap-4 font-600 text-Gray-600 typo-caption1',
        className,
      )}
    >
      <span className="relative mb-4 block size-48">
        <Image
          src={imageUrl}
          alt={specialtyName}
          width={48}
          height={48}
          className="size-48 object-contain"
          loading="lazy"
        />
        {isNew && (
          <div className="absolute bottom-[-6px] left-1/2 -translate-x-1/2">
            <NewBadge dimensions="H16" />
          </div>
        )}
      </span>
      <p className="line-clamp-1 text-center">{institutionName}</p>
    </CustomLink>
  );
};

interface RegionalSpecialtiesSectionProps {
  list: ListHomeLoveMainInstitutionSpecialtyDto[];
  className?: string;
}

export const RegionalSpecialtiesSection = ({
  list,
  className,
}: RegionalSpecialtiesSectionProps) => {
  const [progressPercent, setProgressPercent] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const swiperRef = useRef<HTMLDivElement>(null);

  const items = list.map((item) => ({
    imageUrl: item.specialtyImageUrl,
    specialtyName: item.specialtyName,
    institutionName: item.institutionName,
    href: DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: item.institutionId,
    }),
    isNew: item.newDisplayYN,
  }));

  //* Intersection Observer로 화면에 보이는지 감지
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.8 }, // 80% 보일 때 트리거
    );

    // ref 값을 변수에 저장하여 클린업 함수에서 안전하게 사용
    const currentElement = swiperRef.current;

    if (currentElement) {
      observer.observe(currentElement);
    }

    return () => {
      if (currentElement) {
        observer.unobserve(currentElement);
      }
    };
  }, [isVisible]);

  //* 전체를 포함한 모든 지자체 랜덤 목록
  const sortedItems = useMemo(() => sampleSize(items, items.length), [items]);

  //* 두 줄로 표시할지 여부
  const isTwoRow = sortedItems.length > 10;

  //* 두 줄로 표시할지 여부에 따라 렌더링 할 아이템 목록 생성
  const renderItems = useMemo(() => {
    const result: SpecialtiesItemProps[][] = [];

    if (!isTwoRow) {
      return sortedItems.map((item) => [item]);
    }

    for (let i = 0; i < sortedItems.length; i += 2) {
      result.push(sortedItems.slice(i, i + 2));
    }

    return result;
  }, [sortedItems, isTwoRow]);

  const swiperScrollProgress = (() => {
    if (progressPercent >= SCROLL_PROGRESS_MAX_LEFT) {
      return SCROLL_PROGRESS_MAX_LEFT;
    }
    if (progressPercent <= SCROLL_PROGRESS_MIN_LEFT) {
      return SCROLL_PROGRESS_MIN_LEFT;
    }

    return progressPercent;
  })();

  return (
    <section
      className={cn(
        'flex w-full flex-col items-center gap-20 overflow-hidden pb-32',
        className,
      )}
    >
      <div
        ref={swiperRef}
        className={cn(
          'flex w-full overflow-visible',
          styles.wiggleContainer,
          isVisible && styles.animate,
        )}
      >
        <HorizontalScroll
          className="mx-auto px-20"
          slideClassName="gap-12"
          onProgress={(progress) => {
            setProgressPercent(progress * PROGRESS_MAX_PERCENT);
          }}
        >
          {renderItems.map((item, index) => (
            <article key={index} className="flex flex-col gap-14">
              {item.map((item, index) => (
                <SpecialtiesItem key={index} {...item} />
              ))}
            </article>
          ))}
        </HorizontalScroll>
      </div>

      <div className="relative h-3 w-60 overflow-hidden rounded-pill bg-Gray-300">
        <div
          className="absolute left-0 top-0 h-full rounded-pill bg-Gray-600 transition-all duration-75"
          style={{
            width: `${SCROLL_WIDTH}%`,
            left: `${swiperScrollProgress}%`,
          }}
        />
      </div>
    </section>
  );
};

const PROGRESS_MAX_PERCENT = 100;
const SCROLL_WIDTH = 20;
const SCROLL_PROGRESS_MAX_LEFT = PROGRESS_MAX_PERCENT - SCROLL_WIDTH;
const SCROLL_PROGRESS_MIN_LEFT = 0;
